import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import api from "../lib/api";
import {BusinessSeatMapItem} from "../types/seatmap";
import {SeatMap} from "../types/event";

/** Library seat maps available to a business (each item's `json` is null). */
export function useBusinessSeatMaps(businessId: string | null) {
    return useQuery<BusinessSeatMapItem[]>({
        queryKey: ["business-seatmaps", businessId],
        queryFn: () =>
            api.get(`/api/v1/business/${businessId}/seatmaps`).then((r) => r.data),
        enabled: !!businessId,
        staleTime: 20_000,
        refetchOnWindowFocus: false,
    });
}

/** The seat map currently assigned to an event (name, json, categories, takenSeats). */
export function useEventSeatMap(
    businessId: string | null,
    eventId: string | null,
    seatMapId: string | null
) {
    return useQuery<SeatMap>({
        queryKey: ["event-seatmap", businessId, eventId, seatMapId],
        queryFn: () =>
            api
                .get(`/api/v1/business/${businessId}/events/${eventId}/seatmap/${seatMapId}`)
                .then((r) => r.data as SeatMap),
        enabled: !!businessId && !!eventId && !!seatMapId,
        staleTime: 10_000,
        refetchOnWindowFocus: false,
    });
}

export function useAssignSeatMap(businessId: string | null, eventId: string | null) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (seatMapId: string) =>
            api.put(`/api/v1/business/${businessId}/events/${eventId}/seatmap/${seatMapId}`),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: ["event-seatmap", businessId, eventId]});
        },
    });
}

export function useDetachSeatMap(businessId: string | null, eventId: string | null) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () =>
            api.delete(`/api/v1/business/${businessId}/events/${eventId}/seatmap`),
        onSuccess: () => {
            qc.invalidateQueries({queryKey: ["event-seatmap", businessId, eventId]});
        },
    });
}
